import React, { useState, useEffect } from 'react';
import { useZones } from '../hooks/useZones';
import { useBoxes } from '../hooks/useBoxes';
import { MapPin, Package } from 'lucide-react';

interface LocationSelectorProps {
  currentZoneId?: string | number;
  currentBoxId?: string | number;
  onLocationChange: (zoneId?: string, boxId?: string) => void;
  disabled?: boolean;
  showCurrentLocation?: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  currentZoneId,
  currentBoxId,
  onLocationChange,
  disabled = false,
  showCurrentLocation = true,
}) => {
  const { data: zones = [] } = useZones();
  const { data: boxes = [] } = useBoxes();

  const [locationType, setLocationType] = useState<'zone' | 'box'>('zone');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedBox, setSelectedBox] = useState('');
  
  // Fixer l'emplacement initial (ne change pas après)
  const [initialZoneId] = useState(currentZoneId);
  const [initialBoxId] = useState(currentBoxId);

  // Initialiser avec les valeurs actuelles (une seule fois)
  useEffect(() => {
    if (initialZoneId) {
      setLocationType('zone');
      setSelectedZone(String(initialZoneId));
    } else if (initialBoxId) {
      setLocationType('box');
      setSelectedBox(String(initialBoxId));
    }
  }, [initialZoneId, initialBoxId]);

  const handleLocationTypeChange = (type: 'zone' | 'box') => {
    setLocationType(type);
    setSelectedZone('');
    setSelectedBox('');
  };

  const handleZoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedZone(value);
    onLocationChange(value, undefined);
  };

  const handleBoxChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedBox(value);
    onLocationChange(undefined, value);
  };

  // Trouver l'emplacement actuel pour affichage (utilise les valeurs initiales)
  const getCurrentLocation = () => {
    if (initialZoneId) {
      const zone = zones.find((z: any) => z.id === Number(initialZoneId));
      return zone ? `🏠 ${zone.code} - ${zone.nom}` : `Zone ${initialZoneId}`;
    } else if (initialBoxId) {
      const box = boxes.find((b: any) => b.id === Number(initialBoxId));
      return box ? `📦 ${box.code} - ${box.nom}` : `Box ${initialBoxId}`;
    }
    return 'Aucun emplacement défini';
  };

  return (
    <div className="space-y-4">
      {/* Emplacement actuel */}
      {showCurrentLocation && (initialZoneId || initialBoxId) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-medium text-blue-900 mb-1">📍 Emplacement actuel</p>
          <p className="text-sm text-blue-800 font-medium">{getCurrentLocation()}</p>
        </div>
      )}

      {/* Sélecteur de type d'emplacement */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type d'emplacement
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => !disabled && handleLocationTypeChange('zone')}
            disabled={disabled}
            className={`p-4 border-2 rounded-lg transition-all ${
              locationType === 'zone'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <MapPin className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-sm font-medium text-gray-900">Zone</div>
            <div className="text-xs text-gray-600">Armoire fixe</div>
          </button>
          <button
            type="button"
            onClick={() => !disabled && handleLocationTypeChange('box')}
            disabled={disabled}
            className={`p-4 border-2 rounded-lg transition-all ${
              locationType === 'box'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Package className="h-6 w-6 mx-auto mb-2 text-purple-600" />
            <div className="text-sm font-medium text-gray-900">Box</div>
            <div className="text-xs text-gray-600">Caisse mobile</div>
          </button>
        </div>
      </div>

      {/* Sélecteur de zone */}
      {locationType === 'zone' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zone de destination
          </label>
          <select
            value={selectedZone}
            onChange={handleZoneChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option key="empty-zone" value="">Sélectionner une zone</option>
            {zones.map((zone: any) => (
              <option key={zone.id} value={zone.id}>
                {zone.code} - {zone.nom}
              </option>
            ))}
          </select>
          {zones.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Aucune zone disponible</p>
          )}
        </div>
      )}

      {/* Sélecteur de box */}
      {locationType === 'box' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Box de destination
          </label>
          <select
            value={selectedBox}
            onChange={handleBoxChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option key="empty-box" value="">Sélectionner une box</option>
            {boxes.map((box: any) => (
              <option key={box.id} value={box.id}>
                {box.code} - {box.nom}
              </option>
            ))}
          </select>
          {boxes.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Aucune box disponible</p>
          )}
        </div>
      )}

      {/* Message d'information */}
      {!disabled && (selectedZone || selectedBox) && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800 flex items-center space-x-2">
            <span>✅</span>
            <span>
              Nouvel emplacement sélectionné. Enregistrez pour confirmer le déplacement.
            </span>
          </p>
        </div>
      )}

      {disabled && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            🔒 Vous n'avez pas la permission de déplacer cet objet.
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;

